import { createContext, useContext, useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { getProfile } from "../lib/api";

const SESSION_KEY = "hub_session";
const SESSION_TTL = 30 * 24 * 60 * 60 * 1000; // 30 days
const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  async function loadUser(id) {
    const { data, error } = await supabase
      .from("emilly_users")
      .select("id, email, name, role, created_at")
      .eq("id", id)
      .single();
    if (error || !data) return null;
    return data;
  }

  useEffect(() => {
    const stored = localStorage.getItem(SESSION_KEY);
    if (stored) {
      try {
        const { userId, expiresAt } = JSON.parse(stored);
        if (expiresAt && Date.now() > expiresAt) {
          localStorage.removeItem(SESSION_KEY);
          setLoading(false);
          return;
        }
        loadUser(userId).then((u) => {
          if (!u) {
            localStorage.removeItem(SESSION_KEY);
          } else {
            setUser(u);
            fetchProfile(u.id);
          }
          setLoading(false);
        });
      } catch {
        localStorage.removeItem(SESSION_KEY);
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, []);

  async function fetchProfile(userId) {
    if (!userId) return;
    try {
      const p = await getProfile(userId);
      setProfile(p);
    } catch (e) {
      console.error("[Auth] Error fetching profile:", e);
    }
  }

  async function login(email, password) {
    const res = await fetch("/api/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "Credenciais inválidas");

    const safeUser = data.user;
    setUser(safeUser);
    localStorage.setItem(SESSION_KEY, JSON.stringify({ userId: safeUser.id, expiresAt: Date.now() + SESSION_TTL }));
    await fetchProfile(safeUser.id);
    return safeUser;
  }

  function logout() {
    localStorage.removeItem(SESSION_KEY);
    setUser(null);
    setProfile(null);
  }

  const refreshProfile = () => fetchProfile(user?.id);

  async function updateUserState(newData) {
    const updated = { ...user, ...newData };
    setUser(updated);
    await fetchProfile(user?.id);
  }

  return (
    <AuthCtx.Provider value={{ user, profile, loading, login, logout, refreshProfile, updateUserState }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  return useContext(AuthCtx);
}
